import type {
  ConnectOptions as TwilioConnectOptions,
  CreateLocalAudioTrackOptions as TwilioCreateLocalAudioTrackOptions,
  CreateLocalTrackOptions as TwilioCreateLocalTrackOptions,
} from "twilio-video";
import { isDevMode } from "../../config";
import { logger } from "../../datadog/logger";
import { DeviceType, Env } from "../../types";
import { deviceIsMobile, getEnvFromHostName } from "../../utils";
import { MediaSource, QualityMode } from "../core/room/participant/media/types";
import { serializeMediaMetadata } from "../core/room/participant/media/utils";

// TODO: Consolidate these constants into the getTwilioConfig function
const DESKTOP_ASPECT_RATIO = 16 / 9;
const LOCAL_NQL_DETAIL_LEVEL = 3; // 3 is max
const REMOTE_NQL_DETAIL_LEVEL = 3;
export const IS_DEV_CONNECTION_SETTINGS_ENABLED_KEY =
  "is-dev-connection-settings-enabled";
export const DEV_CONNECTION_SETTINGS_KEY = "dev-connection-settings";
let isH264Supported: boolean;

type AudioVideoSettingsByQualityMode = Record<
  QualityMode,
  Record<
    DeviceType,
    {
      video: Record<
        MediaSource.Camera | MediaSource.Screen,
        TwilioCreateLocalTrackOptions
      >;
      audio: Record<
        MediaSource.Microphone | MediaSource.Screen,
        TwilioCreateLocalAudioTrackOptions
      >;
    }
  >
>;

export const audioVideoSettingsByQualityMode: AudioVideoSettingsByQualityMode =
  {
    [QualityMode.High]: {
      // https://www.twilio.com/docs/video/tutorials/developing-high-quality-video-applications#desktop-browser-grid-recommended-settings
      [DeviceType.Mobile]: {
        video: {
          [MediaSource.Camera]: {
            // 480p
            height: { ideal: 480 },
            frameRate: { ideal: 24 },
            width: { ideal: 640 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.High,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.High,
              source: MediaSource.Microphone,
            }),
          },
          [MediaSource.Screen]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.High,
              source: MediaSource.Screen,
            }),
          },
        },
      },
      // https://www.twilio.com/docs/video/tutorials/developing-high-quality-video-applications#mobile-browser-grid-recommended-settings
      [DeviceType.Desktop]: {
        video: {
          [MediaSource.Camera]: {
            // 720p
            aspectRatio: DESKTOP_ASPECT_RATIO,
            height: { ideal: 720 },
            frameRate: { ideal: 24 },
            width: { ideal: 1280 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.High,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.High,
              source: MediaSource.Microphone,
            }),
          },
          [MediaSource.Screen]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.High,
              source: MediaSource.Screen,
            }),
          },
        },
      },
    },
    [QualityMode.Medium]: {
      [DeviceType.Mobile]: {
        video: {
          [MediaSource.Camera]: {
            // 360p
            height: { ideal: 360 },
            frameRate: { ideal: 24 },
            width: { ideal: 480 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Medium,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Medium,
              source: MediaSource.Microphone,
            }),
          },
          [MediaSource.Screen]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Medium,
              source: MediaSource.Screen,
            }),
          },
        },
      },
      [DeviceType.Desktop]: {
        video: {
          [MediaSource.Camera]: {
            // 360p
            aspectRatio: DESKTOP_ASPECT_RATIO,
            height: { ideal: 360 },
            frameRate: { ideal: 24 },
            width: { ideal: 640 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.Medium,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {},
          [MediaSource.Screen]: {},
        },
      },
    },
    [QualityMode.Low]: {
      [DeviceType.Mobile]: {
        video: {
          // 240p
          [MediaSource.Camera]: {
            height: { ideal: 240 },
            frameRate: { ideal: 24 },
            width: { ideal: 320 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Low,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Low,
              source: MediaSource.Microphone,
            }),
          },
          [MediaSource.Screen]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Mobile,
              qualityMode: QualityMode.Low,
              source: MediaSource.Screen,
            }),
          },
        },
      },
      [DeviceType.Desktop]: {
        video: {
          [MediaSource.Camera]: {
            // 360p
            aspectRatio: DESKTOP_ASPECT_RATIO,
            height: { ideal: 360 },
            frameRate: { ideal: 24 },
            width: { ideal: 640 },
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.Low,
              source: MediaSource.Camera,
            }),
          },
          [MediaSource.Screen]: {},
        },
        audio: {
          [MediaSource.Microphone]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.Low,
              source: MediaSource.Microphone,
            }),
          },
          [MediaSource.Screen]: {
            name: serializeMediaMetadata({
              deviceType: DeviceType.Desktop,
              qualityMode: QualityMode.Low,
              source: MediaSource.Screen,
            }),
          },
        },
      },
    },
  };

/**
 * Test support for H264 codec. Adapted from
 * https://www.twilio.com/docs/video/managing-codecs
 *
 * Some mobile browsers have trouble with H264:
 * https://developer.mozilla.org/en-US/docs/Web/Media/Formats/WebRTC_codecs#supported_video_codecs
 */
export async function testH264Support() {
  if (typeof isH264Supported === "boolean") {
    return isH264Supported;
  }
  if (
    typeof RTCRtpSender !== "undefined" &&
    typeof RTCRtpSender.getCapabilities === "function"
  ) {
    isH264Supported = !!RTCRtpSender.getCapabilities("video")?.codecs.find(
      ({ mimeType }) => mimeType === "video/H264",
    );
    logger.info(`H264 support`, { isH264Supported });
    return isH264Supported;
  }
  if (typeof RTCPeerConnection === "undefined") {
    isH264Supported = false;
    logger.info("H264 support", { isH264Supported });
    return isH264Supported;
  }

  const offerOptions: RTCOfferOptions = {};
  const pc = new RTCPeerConnection();
  try {
    pc.addTransceiver("video");
  } catch (e) {
    offerOptions.offerToReceiveVideo = true;
  }

  try {
    const offer = await pc.createOffer(offerOptions);
    isH264Supported = /^a=rtpmap:.+ H264/m.test(offer.sdp ?? "");
    pc.close();
  } catch (e) {
    isH264Supported = false;
  }
  logger.info("H264 support", { isH264Supported });
  return isH264Supported;
}

function getDevConnectionSettings(): Omit<
  TwilioConnectOptions,
  "video" | "audio"
> {
  if (!isDevMode) return {};
  try {
    const isDevConnectionSettingsEnabled = !!parseInt(
      localStorage.getItem(IS_DEV_CONNECTION_SETTINGS_ENABLED_KEY) ?? "0",
    );
    if (!isDevConnectionSettingsEnabled) return {};
    const rawSettings = localStorage.getItem(DEV_CONNECTION_SETTINGS_KEY);
    if (!rawSettings) return {};
    const settings = JSON.parse(rawSettings);
    logger.debug("Using dev connection settings", settings);
    return settings;
  } catch (error) {
    logger.debug("Error parsing dev connection settings", {}, error as Error);
    return {};
  }
}

/**
 * Returns Twilio connection settings used when connecting to a room.
 *
 * IMPORTANT: Please do not rely on this function for specifying
 * video/audio-track-creation settings. Use the settings in the `tracks` file
 * instead.
 */
export function getConnectSettings(): Omit<
  TwilioConnectOptions,
  "video" | "audio"
> {
  return {
    preferredVideoCodecs: "auto",
    networkQuality: {
      local: LOCAL_NQL_DETAIL_LEVEL,
      remote: REMOTE_NQL_DETAIL_LEVEL,
    },
    maxAudioBitrate: 16000,
    bandwidthProfile: {
      video: {
        mode: "presentation",
        trackSwitchOffMode: "disabled",
        // https://www.twilio.com/docs/video/tutorials/developing-high-quality-video-applications#desktop-browser-grid-recommended-settings
        ...(deviceIsMobile() ? { maxSubscriptionBitrate: 2500000 } : {}),
      },
    },
    ...getDevConnectionSettings(),
  };
}

/**
 * Returns the Twilio config for the current environment.
 */
export function getTwilioConfig() {
  const env = getEnvFromHostName(window.location.hostname);
  switch (env) {
    case Env.PROD:
      return {
        maxParticipants: 5,
      };
    default:
      return {
        maxParticipants: 15,
      };
  }
}
